import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAssessmentService } from "../apis/assessment/useAssessmentService";
import {
  TAssessmentFramework,
  TCreateAssessmentFrameworkRequest,
  TUpdateAssessmentFrameworkRequest,
  TCreateAcademicAssessmentRequest,
  TUpdateAcademicAssessmentRequest,
  TGetAcademicAssessmentResponse,
  TAcademicAssessmentListQuery,
  TPaginatedAcademicAssessments,
  TScoringStructure,
  TCreateScoringStructureRequest,
  TUpdateScoringStructureRequest,
  TGradeCriteria,
  TCreateGradeCriteriaRequest,
  TUpdateGradeCriteriaRequest,
  TEvaluationFramework,
  TCreateEvaluationFrameworkRequest,
  TUpdateEvaluationFrameworkRequest,
} from "../apis/assessment/assessment.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useAssessmentFrameworkHook = (options?: { enabled?: boolean }) => {
  const { getAssessmentFrameworks } = useAssessmentService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TAssessmentFramework[]>({
    queryKey: ["assessment-frameworks", selectedInstitute?.id],
    queryFn: () => getAssessmentFrameworks(selectedInstitute?.id),
    enabled: (options?.enabled ?? true) && !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAcademicAssessmentsHook = (
  listQuery?: TAcademicAssessmentListQuery,
  options?: { enabled?: boolean },
) => {
  const { getAcademicAssessments } = useAssessmentService();
  const { selectedInstitute } = useInstituteContext();

  const mergedQuery: TAcademicAssessmentListQuery = {
    ...listQuery,
    instituteId: listQuery?.instituteId ?? selectedInstitute?.id,
  };

  return useQuery<TPaginatedAcademicAssessments>({
    queryKey: ["academic-assessments", selectedInstitute?.id, listQuery],
    queryFn: () => getAcademicAssessments(mergedQuery),
    enabled: (options?.enabled ?? true) && !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateAcademicAssessmentHook = () => {
  const { createAcademicAssessment } = useAssessmentService();
  const queryClient = useQueryClient();

  return useMutation<
    TGetAcademicAssessmentResponse,
    Error,
    TCreateAcademicAssessmentRequest
  >({
    mutationFn: (request: TCreateAcademicAssessmentRequest) =>
      createAcademicAssessment(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-assessments"] });
    },
  });
};

export const useUpdateAcademicAssessmentHook = () => {
  const { updateAcademicAssessment } = useAssessmentService();
  const queryClient = useQueryClient();

  return useMutation<
    TGetAcademicAssessmentResponse,
    Error,
    { id: string; request: TUpdateAcademicAssessmentRequest }
  >({
    mutationFn: ({ id, request }) => updateAcademicAssessment(id, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["academic-assessments"] });
      queryClient.invalidateQueries({
        queryKey: ["academic-assessment", variables.id],
      });
    },
  });
};

export const useAcademicAssessmentByIdHook = (
  id?: string,
  options?: { enabled?: boolean },
) => {
  const { getAcademicAssessmentById } = useAssessmentService();

  return useQuery<TGetAcademicAssessmentResponse>({
    queryKey: ["academic-assessment", id],
    queryFn: () => getAcademicAssessmentById(id!),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssessmentFrameworkByIdHook = (id?: string) => {
  const { getAssessmentFrameworkById } = useAssessmentService();

  return useQuery<TAssessmentFramework>({
    queryKey: ["assessment-framework", id],
    queryFn: () => getAssessmentFrameworkById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateAssessmentFrameworkHook = () => {
  const { createAssessmentFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TAssessmentFramework,
    Error,
    TCreateAssessmentFrameworkRequest
  >({
    mutationFn: (request: TCreateAssessmentFrameworkRequest) =>
      createAssessmentFramework(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assessment-frameworks", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateAssessmentFrameworkHook = () => {
  const { updateAssessmentFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TAssessmentFramework,
    Error,
    { id: string; request: TUpdateAssessmentFrameworkRequest }
  >({
    mutationFn: ({ id, request }) => updateAssessmentFramework(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assessment-frameworks", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteAssessmentFrameworkHook = () => {
  const { deleteAssessmentFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteAssessmentFramework(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assessment-frameworks", selectedInstitute?.id],
      });
    },
  });
};

// Story 7.3: Scoring Structure Hooks
export const useScoringStructureHook = (classId?: string) => {
  const { getScoringStructures } = useAssessmentService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TScoringStructure[]>({
    queryKey: ["scoring-structures", selectedInstitute?.id, classId],
    queryFn: () => getScoringStructures(selectedInstitute?.id, classId),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useScoringStructureByIdHook = (id?: string) => {
  const { getScoringStructureById } = useAssessmentService();

  return useQuery<TScoringStructure>({
    queryKey: ["scoring-structure", id],
    queryFn: () => getScoringStructureById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateScoringStructureHook = () => {
  const { createScoringStructure } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TScoringStructure, Error, TCreateScoringStructureRequest>({
    mutationFn: (request: TCreateScoringStructureRequest) =>
      createScoringStructure(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-structures", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateScoringStructureHook = () => {
  const { updateScoringStructure } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TScoringStructure,
    Error,
    { id: string; request: TUpdateScoringStructureRequest }
  >({
    mutationFn: ({ id, request }) => updateScoringStructure(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-structures", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteScoringStructureHook = () => {
  const { deleteScoringStructure } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteScoringStructure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-structures", selectedInstitute?.id],
      });
    },
  });
};

// Story 7.4: Grade Criteria Hooks
export const useGradeCriteriaHook = (gradeLevelId?: string) => {
  const { getGradeCriteria } = useAssessmentService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TGradeCriteria[]>({
    queryKey: ["grade-criteria", selectedInstitute?.id, gradeLevelId],
    queryFn: () => getGradeCriteria(selectedInstitute?.id, gradeLevelId),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGradeCriteriaByIdHook = (id?: string) => {
  const { getGradeCriteriaById } = useAssessmentService();

  return useQuery<TGradeCriteria>({
    queryKey: ["grade-criteria", id],
    queryFn: () => getGradeCriteriaById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateGradeCriteriaHook = () => {
  const { createGradeCriteria } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TGradeCriteria, Error, TCreateGradeCriteriaRequest>({
    mutationFn: (request: TCreateGradeCriteriaRequest) =>
      createGradeCriteria(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["grade-criteria", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateGradeCriteriaHook = () => {
  const { updateGradeCriteria } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TGradeCriteria,
    Error,
    { id: string; request: TUpdateGradeCriteriaRequest }
  >({
    mutationFn: ({ id, request }) => updateGradeCriteria(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["grade-criteria", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteGradeCriteriaHook = () => {
  const { deleteGradeCriteria } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteGradeCriteria(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["grade-criteria", selectedInstitute?.id],
      });
    },
  });
};

// Story 7.5: Evaluation Framework Hooks
export const useEvaluationFrameworkHook = (classId?: string) => {
  const { getEvaluationFrameworks } = useAssessmentService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TEvaluationFramework[]>({
    queryKey: ["evaluation-frameworks", selectedInstitute?.id, classId],
    queryFn: () => getEvaluationFrameworks(selectedInstitute?.id, classId),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useEvaluationFrameworkByIdHook = (id?: string) => {
  const { getEvaluationFrameworkById } = useAssessmentService();

  return useQuery<TEvaluationFramework>({
    queryKey: ["evaluation-framework", id],
    queryFn: () => getEvaluationFrameworkById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateEvaluationFrameworkHook = () => {
  const { createEvaluationFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TEvaluationFramework,
    Error,
    TCreateEvaluationFrameworkRequest
  >({
    mutationFn: (request: TCreateEvaluationFrameworkRequest) =>
      createEvaluationFramework(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["evaluation-frameworks", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateEvaluationFrameworkHook = () => {
  const { updateEvaluationFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TEvaluationFramework,
    Error,
    { id: string; request: TUpdateEvaluationFrameworkRequest }
  >({
    mutationFn: ({ id, request }) => updateEvaluationFramework(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["evaluation-frameworks", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteEvaluationFrameworkHook = () => {
  const { deleteEvaluationFramework } = useAssessmentService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteEvaluationFramework(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["evaluation-frameworks", selectedInstitute?.id],
      });
    },
  });
};
